/**
 * Core move implementations for the Legendary Arena game engine.
 *
 * Each move follows the three-step ordering:
 *   1. Validate args (call validator from WP-008A)
 *   2. Check stage gate (call validateMoveAllowedInStage from WP-008A)
 *   3. Mutate G (via zoneOps.ts helpers)
 *
 * Moves never throw. Invalid input causes an early return (void).
 * All randomness uses ctx.random exclusively — never Math.random().
 */
import { validateDrawCardsArgs, validatePlayCardArgs, validateMoveAllowedInStage } from './coreMoves.validate.js';
import { moveCardFromZone } from './zoneOps.js';
import { applyEndOfTurnCleanup } from './endOfTurnCleanup.logic.js';
import { HAND_SIZE, drawCardsIntoHand } from './drawCards.logic.js';
import { addResources } from '../economy/economy.logic.js';
import { executeHeroEffects } from '../hero/heroEffects.execute.js';
import { applyGrantedOutOfTime } from './outOfTimeGrant.logic.js';
import { hasPendingKoHeroChoice } from './koHeroChoice.resolve.js';
import { hasPendingScryKoChoice } from './scryKoChoice.resolve.js';
import { hasPendingMelterKoChoice } from './melterKoChoice.resolve.js';
import { hasPendingRuthlessDictatorChoice } from './ruthlessDictatorChoice.resolve.js';
import { hasPendingElectromagneticBubbleChoice } from './electromagneticBubbleChoice.resolve.js';
import { hasPendingDiscardChoice } from './discardChoice.resolve.js';
import { hasPendingPutCardsOnDeckChoice } from './putCardsOnDeckChoice.resolve.js';
import { hasPendingKoDiscardChoice } from './koDiscardChoice.resolve.js';
import { hasPendingReorderChoice } from './reorderChoice.resolve.js';
import { hasPendingDefeatChoice } from './defeatChoice.resolve.js';
import { hasPendingOptionalKoReward } from './optionalKoReward.resolve.js';
import { hasPendingSmashDiscard } from './smashDiscard.resolve.js';
import { hasPendingPutHandOnDeckTop } from './putHandOnDeckTop.resolve.js';
import { hasPendingRevealTopDispose } from './revealTopDispose.resolve.js';
import { hasPendingRevealThreeAssign } from './revealThreeAssign.resolve.js';
import { hasPendingDoOver } from './doOver.resolve.js';
import { hasPendingPlayVillainTopChoice } from './playVillainTop.resolve.js';
import { hasPendingVictoryPileCardPick } from './resolveVictoryPileCardPick.js';
import { hasPendingDrawOrEmpowered } from './drawOrEmpowered.resolve.js';
import { hasPendingCoveringFireChoice } from './coveringFireChoice.resolve.js';
import { hasPendingMoveVillainChoice } from './moveVillainChoice.resolve.js';
import { hasPendingSplitFaceChoice, isSplitCardInstance, parkSplitFaceChoice, playBothSplitFaces } from './splitFaceChoice.resolve.js';
import { hasPendingCountScaledChoice } from './countScaledChoice.resolve.js';
import { hasPendingUndercoverChoice } from './undercover.resolve.js';
import { hasPendingReturnZeroCostDiscard } from './resolveReturnZeroCostDiscard.js';
import { hasPendingDiscardToPlay, getDiscardToPlayCost } from './resolveDiscardToPlay.js';
import { hasPendingReturnOnDiscard } from './resolveReturnOnDiscard.js';
import { hasPendingGiveHqHeroChoice } from './giveHqHeroChoice.resolve.js';
import { hasPendingCopyPowersChoice } from './copyPowersChoice.resolve.js';
import { hasPendingSeatChoice } from './seatChoice.resolve.js';
import { formatBaseEconomyClause, formatPlayedCardLabel } from '../log/logDisplay.js';
import { pushLog } from '../log/logPush.js';
import { SIDEKICK_EXT_ID, WOUND_EXT_ID } from '../setup/pilesInit.js';
import { returnPlayedSidekick } from './recruitSidekick.js';
import { consumeExtraTurn } from '../turn/turnLoop.js';
/**
 * Draws the active player's start-of-turn hand, capped at HAND_SIZE and
 * guarded to once per turn.
 *
 * The start-of-turn hand is normally drawn automatically by the play-phase
 * onBegin auto-draw; this move is kept as defensive, engine-authoritative
 * protection. A direct drawCards submission (including a raw socket message)
 * is capped at HAND_SIZE and blocked after the first draw of the turn via
 * G.hasDrawnThisTurn, so the production over-draw bug cannot recur over the
 * wire. The deck-to-hand fill (with discard reshuffle on exhaustion)
 * delegates to the shared drawCardsIntoHand primitive.
 *
 * @param context - boardgame.io move context with G, events, random, playerID.
 * @param args - DrawCardsArgs payload with a count field.
 */
export function drawCards({ G, playerID, ...context }, args) {
    // Step 1: Validate args
    const argsResult = validateDrawCardsArgs(args);
    if (!argsResult.ok) {
        return;
    }
    // Step 2: Check stage gate
    const stageResult = validateMoveAllowedInStage('drawCards', G.currentStage);
    if (!stageResult.ok) {
        return;
    }
    // why: block-all guard (D-24008) — while a KO-a-Hero choice is pending the
    // board is frozen; this move returns with no side effects. Placed immediately
    // after the stage gate, before any zone/economy access, so a blocked move
    // leaks no partial state.
    if (hasPendingKoHeroChoice(G)) {
        return;
    }
    // why: block-all guard (D-24282) — while a Doombot scry-KO choice is pending
    // the board is frozen; this move returns with no side effects so the player
    // picks which revealed card to KO first. Mirrors the D-24008 KO-hero check above.
    if (hasPendingScryKoChoice(G)) {
        return;
    }
    // why: WP-603 / D-24413 — block-all guard: a pending Melter Fight KO/keep choice
    // freezes the board until the fighting player resolves every revealed deck top.
    if (hasPendingMelterKoChoice(G)) {
        return;
    }
    // why: WP-695 / D-24512 — block-all guard: a pending Ruthless Dictator scry-3
    // disposition choice freezes the board until the defeating player dispositions
    // every revealed card.
    if (hasPendingRuthlessDictatorChoice(G)) {
        return;
    }
    // why: WP-695 / D-24512 — block-all guard: a pending Electromagnetic Bubble X-Men
    // pick freezes the board until the defeating player picks an X-Men Hero.
    if (hasPendingElectromagneticBubbleChoice(G)) {
        return;
    }
    // why: block-all guard (WP-476 / D-24284) — while a discard-to-limit choice is
    // pending the board is frozen; this move returns with no side effects so the
    // current player picks which cards to discard first. Mirrors the D-24282 scry-KO
    // check above.
    if (hasPendingDiscardChoice(G)) {
        return;
    }
    if (hasPendingPutCardsOnDeckChoice(G)) {
        return;
    }
    // why: block-all guard (WP-479 / D-24286) — a pending reveal-remainder reorder
    // freezes the board until the current player picks their deck-top order.
    if (hasPendingReorderChoice(G)) {
        return;
    }
    // why: block-all guard (WP-486 / D-24291) — a pending defeat-with-a-Bystander
    // choice freezes the board until the current player picks which target to defeat.
    if (hasPendingDefeatChoice(G)) {
        return;
    }
    // why: block-all guard (WP-693 / D-24510) — a pending KO-from-discard choice
    // (Loki's Maniacal Tyrant) freezes the board until the active player selects 0..4
    // of their own discard cards to KO.
    if (hasPendingKoDiscardChoice(G)) {
        return;
    }
    // why: block-all guard (D-24019) — optional-KO-reward choice pending; the
    // board is frozen until resolved (beside the D-24008 KO-hero check above).
    if (hasPendingPlayVillainTopChoice(G))
        return; // why: WP-663 / D-24474 — block-all guard (Shadowed Thoughts play-villain-top choice)
    if (hasPendingOptionalKoReward(G)) {
        return;
    }
    // why: WP-676 / D-24492 — block-all guard: a parked Smash discard-for-attack choice
    // freezes the board until the player discards a hand card or declines.
    if (hasPendingSmashDiscard(G)) {
        return;
    }
    // why: WP-700 / D-24519 — block-all guard: a parked put-a-hand-card-on-deck-top choice
    // freezes the board until the player places one hand card on top of their deck (mandatory).
    if (hasPendingPutHandOnDeckTop(G)) {
        return;
    }
    // why: WP-702 / D-24521 — block-all guard: a parked reveal-top discard-or-keep choice
    // freezes the board until the player dispositions every revealed deck top (discard or keep).
    if (hasPendingRevealTopDispose(G)) {
        return;
    }
    // why: WP-753 / D-24580 — block-all guard: a parked reveal-three assignment freezes the
    // board until the player assigns every revealed card (draw / discard / KO).
    if (hasPendingRevealThreeAssign(G)) {
        return;
    }
    // why: WP-681 / D-24498 — block-all guard: a parked Do-Over accept/decline choice
    // freezes the board until the player accepts (discard hand + draw 4) or declines.
    if (hasPendingDoOver(G)) {
        return;
    }
    // why: block-all — pendingVictoryPileCardPick must be resolved before any other action (D-24067)
    if (hasPendingVictoryPileCardPick(G)) {
        return;
    }
    // why: block-all — pendingDrawOrEmpowered must be resolved before any other action (D-24069)
    if (hasPendingDrawOrEmpowered(G)) {
        return;
    }
    // why: block-all — pendingCoveringFireChoices must be resolved before any other action (WP-719 / D-24541)
    if (hasPendingCoveringFireChoice(G)) {
        return;
    }
    // why: block-all — pendingMoveVillainChoices must be resolved before any other action (WP-795 / D-24664)
    if (hasPendingMoveVillainChoice(G)) {
        return;
    }
    // why: block-all — pendingSplitFaceChoices must be resolved before any other action (WP-724 / D-24546)
    if (hasPendingSplitFaceChoice(G)) {
        return;
    }
    // why: block-all -- pendingCountScaledChoice must be resolved before any other action (WP-675 / D-24490)
    if (hasPendingCountScaledChoice(G)) {
        return;
    }
    // why: block-all — pendingUndercoverChoice must be resolved before any other action (WP-678 / D-24494)
    if (hasPendingUndercoverChoice(G)) {
        return;
    }
    // why: block-all — pendingReturnZeroCostDiscard must be resolved before any other action (D-24139)
    if (hasPendingReturnZeroCostDiscard(G)) {
        return;
    }
    // why: block-all — pendingDiscardToPlay must be resolved before any other action (WP-383 / D-24184)
    if (hasPendingDiscardToPlay(G)) {
        return;
    }
    // why: block-all — pendingReturnOnDiscard must be resolved before any other action (WP-498 / D-24301)
    if (hasPendingReturnOnDiscard(G)) {
        return;
    }
    // why: block-all — pendingGiveHqHeroChoice (interactive give-HQ-Hero pick, Paibok Fight) must be resolved before any other action (WP-532 / D-24343)
    if (hasPendingGiveHqHeroChoice(G)) {
        return;
    }
    // why: block-all — pendingCopyPowersChoice (interactive copy-a-Hero pick, Rogue's Copy Powers) must be resolved before any other action (WP-535 / D-24345)
    if (hasPendingCopyPowersChoice(G)) {
        return;
    }
    // why: block-all — a non-active/multi-seat pending seat choice freezes every action move
    // until all addressed seats resolve (only resolveSeatChoice proceeds) (WP-684 / D-24501)
    if (hasPendingSeatChoice(G)) {
        return;
    }
    // why: once-per-turn guard — the start-of-turn draw is consumed once per turn
    // (mirrors revealVillainCard). A second drawCards submission in the same turn,
    // from any source, is a silent no-op. Card-effect draws (the hero `draw`
    // keyword) use the rule-effect path (applyDrawCards) and bypass this guard.
    // Placed before resolving zones / any reshuffle so a blocked move consumes no
    // RNG and leaves G untouched.
    if (G.hasDrawnThisTurn) {
        return;
    }
    // Step 3: Mutate G
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    // why: cap the draw to HAND_SIZE — the authoritative, race-free cap the
    // deleted UI scaffold could only approximate. The deck-to-hand fill with
    // reshuffle delegates to the shared drawCardsIntoHand primitive.
    const cardsToDraw = Math.min(args.count, Math.max(0, HAND_SIZE - playerZones.hand.length));
    drawCardsIntoHand(playerZones, cardsToDraw, context);
    // why: the draw attempt is consumed regardless of how many cards were drawn
    // (even zero — an empty deck/discard or an already-full hand still spends the
    // turn's draw allowance), foreclosing an empty-deck retry loop.
    G.hasDrawnThisTurn = true;
}
/**
 * Shared card-play core: appends an already-resolved card to the player's
 * inPlay zone, adds its base attack/recruit economy, and fires its onPlay hero
 * effects. The caller is responsible for removing the card from its source zone
 * first (hand, for playCard).
 *
 * // why: extracting the post-removal play steps into one shared function keeps any
 * // future alternate play path (e.g. play-from-a-zone) byte-identical to hand play
 * // — same inPlay append order, same base economy, same executeHeroEffects. (The
 * // WP-282 playFromUndercover caller was retired with the face-down model in
 * // WP-678 / D-24494; applyCardPlay now has the single hand caller.)
 *
 * @param G - The game state to mutate.
 * @param context - boardgame.io move context rest (carries random for effects).
 * @param playerID - The player playing the card.
 * @param cardId - The ext_id of the card being played (already removed from source).
 */
export function applyCardPlay(G, context, playerID, cardId) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    playerZones.inPlay = [...playerZones.inPlay, cardId];
    // why: MVP adds base values only; conditional bonuses are WP-023
    const cardStats = G.cardStats[cardId];
    const heroAttack = cardStats ? cardStats.attack : 0;
    const heroRecruit = cardStats ? cardStats.recruit : 0;
    G.turnEconomy = addResources(G.turnEconomy, heroAttack, heroRecruit);
    // why: WP-295 / D-24082 — surface every hero card play in the game log
    // (G.messages -> UIState.log); playCard was previously silent. WP-323 enriches
    // the raw ext-id to "{Name} ({ext-id}) — {printed effect}" via
    // formatPlayedCardLabel, resolving the name/effect from G.cardDisplayData
    // (already in scope). Emitted in the shared core so playFromUndercover is
    // covered identically. Cheap: WP-294 excluded G.messages from the
    // finalStateHash oracle (D-24081).
    // why: WP-417 / D-24237 — the printed icons are the WHOLE effect of a starter
    // (S.H.I.E.L.D. Agent / Trooper have no ability text), so the base attack /
    // recruit this play just added is folded into the same line rather than emitted
    // as a second one — one line per play keeps the log readable when six starters
    // are played in a row.
    pushLog(G, `Player ${playerID} played ${formatPlayedCardLabel(G.cardDisplayData, cardId, formatBaseEconomyClause(heroAttack, heroRecruit))}.`, 'neutral', cardId);
    // why: hero ability effects fire immediately after play, before any
    // fight/recruit actions. This preserves "play -> generate resources -> act."
    // Hero hook economy is additive to base card stats above (WP-022).
    // why: WP-409 / D-24221 — capture how many hero effects fired for this play onto
    // a per-turn observability transient. Covers playFromUndercover too (shared core).
    // Feeds UIState.game.lastPlayEffectsFired for the future client tiered combo/synergy
    // cue; excluded from the finalStateHash oracle (observability-only), and not seeded at
    // setup, so no sentinel or PRE_WP080 hash re-pin is required.
    G.lastPlayEffectsFired = executeHeroEffects(G, context, playerID, cardId);
    // why: WP-828 / D-24761 — a hand Hero granted Man Out of Time this turn (Winter Soldier's 2>4)
    // is set aside Out of Time after its abilities, exactly where its own printed keyword would
    // (the last effect of the play). Shared core (hand play and the turn-2 replay); the replay is
    // never re-granted (the grant list is cleared at turn start, and guard 1 blocks a second set-aside).
    applyGrantedOutOfTime(G, context, playerID, cardId);
    // why: WP-842 / D-24776 — a played Sidekick returns to the bottom of the Sidekick Stack
    // and draws two. Last, after Out of Time, so a granted Out of Time set-aside wins (the
    // return only fires while the Sidekick is still the last inPlay entry).
    if (cardId === SIDEKICK_EXT_ID) {
        returnPlayedSidekick(G, context, playerID);
    }
}
/**
 * Plays a card from the active player's hand into their inPlay zone.
 *
 * @param context - boardgame.io move context with G, events, random, playerID.
 * @param args - PlayCardArgs payload with a cardId field.
 */
export function playCard({ G, playerID, ...context }, args) {
    // Step 1: Validate args
    const argsResult = validatePlayCardArgs(args);
    if (!argsResult.ok) {
        return;
    }
    // Step 2: Check stage gate
    const stageResult = validateMoveAllowedInStage('playCard', G.currentStage);
    if (!stageResult.ok) {
        return;
    }
    // why: block-all guard (D-24008) — while a KO-a-Hero choice is pending the
    // board is frozen; this move returns with no side effects. Placed immediately
    // after the stage gate, before any zone/economy access.
    if (hasPendingKoHeroChoice(G)) {
        return;
    }
    // why: block-all guard (D-24282) — while a Doombot scry-KO choice is pending
    // the board is frozen; this move returns with no side effects so the player
    // picks which revealed card to KO first. Mirrors the D-24008 KO-hero check above.
    if (hasPendingScryKoChoice(G)) {
        return;
    }
    // why: WP-603 / D-24413 — block-all guard: a pending Melter Fight KO/keep choice
    // freezes the board until the fighting player resolves every revealed deck top.
    if (hasPendingMelterKoChoice(G)) {
        return;
    }
    // why: WP-695 / D-24512 — block-all guard: a pending Ruthless Dictator scry-3
    // disposition choice freezes the board until the defeating player dispositions
    // every revealed card.
    if (hasPendingRuthlessDictatorChoice(G)) {
        return;
    }
    // why: WP-695 / D-24512 — block-all guard: a pending Electromagnetic Bubble X-Men
    // pick freezes the board until the defeating player picks an X-Men Hero.
    if (hasPendingElectromagneticBubbleChoice(G)) {
        return;
    }
    // why: block-all guard (WP-476 / D-24284) — while a discard-to-limit choice is
    // pending the board is frozen; this move returns with no side effects so the
    // current player picks which cards to discard first. Mirrors the D-24282 scry-KO
    // check above.
    if (hasPendingDiscardChoice(G)) {
        return;
    }
    if (hasPendingPutCardsOnDeckChoice(G)) {
        return;
    }
    // why: block-all guard (WP-479 / D-24286) — a pending reveal-remainder reorder
    // freezes the board until the current player picks their deck-top order.
    if (hasPendingReorderChoice(G)) {
        return;
    }
    // why: block-all guard (WP-486 / D-24291) — a pending defeat-with-a-Bystander
    // choice freezes the board until the current player picks which target to defeat.
    if (hasPendingDefeatChoice(G)) {
        return;
    }
    // why: block-all guard (WP-693 / D-24510) — a pending KO-from-discard choice
    // (Loki's Maniacal Tyrant) freezes the board until the active player selects 0..4
    // of their own discard cards to KO.
    if (hasPendingKoDiscardChoice(G)) {
        return;
    }
    // why: block-all guard (D-24019) — optional-KO-reward choice pending; the
    // board is frozen until resolved (beside the D-24008 KO-hero check above).
    if (hasPendingPlayVillainTopChoice(G))
        return; // why: WP-663 / D-24474 — block-all guard (Shadowed Thoughts play-villain-top choice)
    if (hasPendingOptionalKoReward(G)) {
        return;
    }
    // why: WP-676 / D-24492 — block-all guard: a parked Smash discard-for-attack choice
    // freezes the board until the player discards a hand card or declines.
    if (hasPendingSmashDiscard(G)) {
        return;
    }
    // why: WP-700 / D-24519 — block-all guard: a parked put-a-hand-card-on-deck-top choice
    // freezes the board until the player places one hand card on top of their deck (mandatory).
    if (hasPendingPutHandOnDeckTop(G)) {
        return;
    }
    // why: WP-702 / D-24521 — block-all guard: a parked reveal-top discard-or-keep choice
    // freezes the board until the player dispositions every revealed deck top (discard or keep).
    if (hasPendingRevealTopDispose(G)) {
        return;
    }
    // why: WP-753 / D-24580 — block-all guard: a parked reveal-three assignment freezes the
    // board until the player assigns every revealed card (draw / discard / KO).
    if (hasPendingRevealThreeAssign(G)) {
        return;
    }
    // why: WP-681 / D-24498 — block-all guard: a parked Do-Over accept/decline choice
    // freezes the board until the player accepts (discard hand + draw 4) or declines.
    if (hasPendingDoOver(G)) {
        return;
    }
    // why: block-all — pendingVictoryPileCardPick must be resolved before any other action (D-24067)
    if (hasPendingVictoryPileCardPick(G)) {
        return;
    }
    // why: block-all — pendingDrawOrEmpowered must be resolved before any other action (D-24069)
    if (hasPendingDrawOrEmpowered(G)) {
        return;
    }
    // why: block-all — pendingCoveringFireChoices must be resolved before any other action (WP-719 / D-24541)
    if (hasPendingCoveringFireChoice(G)) {
        return;
    }
    // why: block-all — pendingMoveVillainChoices must be resolved before any other action (WP-795 / D-24664)
    if (hasPendingMoveVillainChoice(G)) {
        return;
    }
    // why: block-all — pendingSplitFaceChoices must be resolved before any other action (WP-724 / D-24546)
    if (hasPendingSplitFaceChoice(G)) {
        return;
    }
    // why: block-all -- pendingCountScaledChoice must be resolved before any other action (WP-675 / D-24490)
    if (hasPendingCountScaledChoice(G)) {
        return;
    }
    // why: block-all — pendingUndercoverChoice must be resolved before any other action (WP-678 / D-24494)
    if (hasPendingUndercoverChoice(G)) {
        return;
    }
    // why: block-all — pendingReturnZeroCostDiscard must be resolved before any other action (D-24139)
    if (hasPendingReturnZeroCostDiscard(G)) {
        return;
    }
    // why: block-all — pendingDiscardToPlay must be resolved before any other action (WP-383 / D-24184)
    if (hasPendingDiscardToPlay(G)) {
        return;
    }
    // why: block-all — pendingReturnOnDiscard must be resolved before any other action (WP-498 / D-24301)
    if (hasPendingReturnOnDiscard(G)) {
        return;
    }
    // why: block-all — pendingGiveHqHeroChoice (interactive give-HQ-Hero pick, Paibok Fight) must be resolved before any other action (WP-532 / D-24343)
    if (hasPendingGiveHqHeroChoice(G)) {
        return;
    }
    // why: block-all — pendingCopyPowersChoice (interactive copy-a-Hero pick, Rogue's Copy Powers) must be resolved before any other action (WP-535 / D-24345)
    if (hasPendingCopyPowersChoice(G)) {
        return;
    }
    // why: block-all — a non-active/multi-seat pending seat choice freezes every action move
    // until all addressed seats resolve (only resolveSeatChoice proceeds) (WP-684 / D-24501)
    if (hasPendingSeatChoice(G)) {
        return;
    }
    // Step 3: Mutate G
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    // why: WP-643 / D-24455 — a Wound has no "play a Wound" path (wiki/wounds.md:
    // Wounds carry no Attack/Recruit and cannot be played; their only interaction
    // is the Healing ability, used directly from hand via healWounds). This is a
    // card-specific PRE-COMMIT precondition (the D-24185 class): a playCard whose
    // target is WOUND_EXT_ID returns void with NO commit — the Wound stays in hand
    // and no economy is granted — before any zone removal. Without this guard a raw
    // socket message (or any non-UI caller) could move the Wound hand → inPlay,
    // where healWounds (which reads the hand only) can no longer reach it. Runs as
    // part of validation, before the hand removal, so the "validate → gate →
    // mutate → void" move contract is preserved. The getLegalMoves enumeration is
    // kept in lockstep (ai.legalMoves.ts skips WOUND_EXT_ID) so the bot never picks
    // a move this precondition would silently reject.
    if (args.cardId === WOUND_EXT_ID) {
        return;
    }
    // why: WP-383 / D-24185 — the engine's first card-specific PRE-COMMIT
    // precondition. A card printed "To play this card, you must discard a card
    // from your hand" (the `discard-to-play` cost) can only be played if the hand
    // holds enough OTHER cards to pay it. The played card is still in hand here,
    // so payable iff hand.length >= cost + 1. An unpayable play returns void with
    // NO commit — the card stays in hand and its base attack/recruit is NOT
    // granted (the bug this fixes: the cost was silently skipped and the power
    // leaked). This runs as part of validation, before the hand removal, so the
    // "validate → gate → mutate → void" move contract is preserved.
    // why: WP-777 / D-24615 — a split card is exempt: its side (and so its cost) is not known
    // until resolveSplitFaceChoice, which binds a side's cost there. Checking here would read
    // the PLAYED id's face — e.g. reject Attune / Atone from a one-card hand though Atone is free.
    const discardToPlayCost = getDiscardToPlayCost(G, args.cardId);
    if (discardToPlayCost > 0 &&
        playerZones.hand.length < discardToPlayCost + 1 &&
        !isSplitCardInstance(G, args.cardId)) {
        pushLog(G, 
        // why: WP-417 — an empty economy clause here: the play was REJECTED, so no
        // base attack/recruit was granted and the line must not imply otherwise.
        `Player ${playerID} could not play ${formatPlayedCardLabel(G.cardDisplayData, args.cardId, '')} — it requires discarding ${discardToPlayCost} card(s) but their hand holds no other card to discard.`, 'neutral', args.cardId);
        return;
    }
    // why: remove the card from hand (first occurrence) with an empty target so
    // the inPlay append happens via the shared applyCardPlay core below. This
    // preserves moveCardFromZone's "remove exactly one copy" semantics for the
    // duplicate-cards case while keeping play behavior identical to
    // playFromUndercover (WP-282 IC-282-02).
    const removal = moveCardFromZone(playerZones.hand, [], args.cardId);
    if (!removal.found) {
        return;
    }
    playerZones.hand = removal.from;
    // why: WP-724 / D-24546 — a split / dual-faced card binds its side at PLAY time. Enter it in
    // inPlay as its primary face, DEFER its base economy + ability, and park a "choose a side"
    // choice; resolveSplitFaceChoice then grants the CHOSEN face's economy and fires its ability
    // (the other face does nothing). A non-split card resolves immediately via applyCardPlay below.
    // The block-all guards above keep the board frozen until the side is chosen.
    // why: WP-780 / D-24619 — cvwr Penumbra overrides rules v23 p.49 ("choose which side to play")
    // for the rest of its turn: a later Divided Card plays BOTH sides as two different cards, so no
    // picker is parked. playBothSplitFaces returns false only on a null pair; that falls through
    // to the park path below so the card (already out of hand) is never lost.
    if (isSplitCardInstance(G, args.cardId) &&
        G.turnEconomy.isPlayBothSidesActive === true &&
        playBothSplitFaces(G, context, playerID, args.cardId)) {
        return;
    }
    if (isSplitCardInstance(G, args.cardId)) {
        playerZones.inPlay = [...playerZones.inPlay, args.cardId];
        parkSplitFaceChoice(G, playerID, args.cardId);
        pushLog(G, 
        // why: WP-417 — empty economy clause: no base attack/recruit is granted yet (deferred to
        // the choice), so the line must not imply otherwise.
        `Player ${playerID} played ${formatPlayedCardLabel(G.cardDisplayData, args.cardId, '')} — choose a side.`, 'neutral', args.cardId);
        return;
    }
    applyCardPlay(G, context, playerID, args.cardId);
}
/**
 * Ends the current player's turn by moving all cards to discard and
 * calling ctx.events.endTurn() to advance to the next player.
 *
 * @param context - boardgame.io move context with G, events, random, playerID.
 */
export function endTurn({ G, playerID, events, random }) {
    // Step 1: Validate args (endTurn has no args — skip to stage gate)
    // Step 2: Check stage gate
    const stageResult = validateMoveAllowedInStage('endTurn', G.currentStage);
    if (!stageResult.ok) {
        return;
    }
    // why: turn cannot end while a player-choice reveal is pending; the player must
    // call resolveHeroChoice first. Guard precedes the zone sweep so a blocked turn
    // does not discard the hand (D-22002)
    if (G.pendingHeroChoice !== undefined) {
        return;
    }
    // why: turn cannot end while a KO-a-Hero choice is pending (D-24008 —
    // queue-non-empty blocks turn-end). Guard precedes the zone sweep so a
    // blocked turn does not discard the hand. Both pending systems must clear
    // before the turn ends (dual-pending coexistence with WP-220).
    if (hasPendingKoHeroChoice(G)) {
        return;
    }
    // why: block-all guard (D-24282) — while a Doombot scry-KO choice is pending
    // the board is frozen; this move returns with no side effects so the player
    // picks which revealed card to KO first. Mirrors the D-24008 KO-hero check above.
    if (hasPendingScryKoChoice(G)) {
        return;
    }
    // why: WP-603 / D-24413 — block-all guard: a pending Melter Fight KO/keep choice
    // freezes the board until the fighting player resolves every revealed deck top.
    if (hasPendingMelterKoChoice(G)) {
        return;
    }
    // why: WP-695 / D-24512 — block-all guard: a pending Ruthless Dictator scry-3
    // disposition choice freezes the board until the defeating player dispositions
    // every revealed card.
    if (hasPendingRuthlessDictatorChoice(G)) {
        return;
    }
    // why: WP-695 / D-24512 — block-all guard: a pending Electromagnetic Bubble X-Men
    // pick freezes the board until the defeating player picks an X-Men Hero.
    if (hasPendingElectromagneticBubbleChoice(G)) {
        return;
    }
    // why: block-all guard (WP-476 / D-24284) — while a discard-to-limit choice is
    // pending the board is frozen; this move returns with no side effects so the
    // current player picks which cards to discard first. Mirrors the D-24282 scry-KO
    // check above.
    if (hasPendingDiscardChoice(G)) {
        return;
    }
    if (hasPendingPutCardsOnDeckChoice(G)) {
        return;
    }
    // why: block-all guard (WP-479 / D-24286) — a pending reveal-remainder reorder
    // freezes the board until the current player picks their deck-top order.
    if (hasPendingReorderChoice(G)) {
        return;
    }
    // why: block-all guard (WP-486 / D-24291) — a pending defeat-with-a-Bystander
    // choice freezes the board until the current player picks which target to defeat.
    if (hasPendingDefeatChoice(G)) {
        return;
    }
    // why: block-all guard (WP-693 / D-24510) — a pending KO-from-discard choice
    // (Loki's Maniacal Tyrant) freezes the board until the active player selects 0..4
    // of their own discard cards to KO.
    if (hasPendingKoDiscardChoice(G)) {
        return;
    }
    // why: turn cannot end while an optional-KO-reward choice is pending (D-24019
    // — queue-non-empty blocks turn-end). Guard precedes the zone sweep so a
    // blocked turn does not discard the hand (beside the D-24008 KO-hero check).
    if (hasPendingPlayVillainTopChoice(G))
        return; // why: WP-663 / D-24474 — block-all guard (Shadowed Thoughts play-villain-top choice)
    if (hasPendingOptionalKoReward(G)) {
        return;
    }
    // why: WP-676 / D-24492 — block-all guard: a parked Smash discard-for-attack choice
    // freezes the board until the player discards a hand card or declines.
    if (hasPendingSmashDiscard(G)) {
        return;
    }
    // why: WP-700 / D-24519 — block-all guard: a parked put-a-hand-card-on-deck-top choice
    // freezes the board until the player places one hand card on top of their deck (mandatory).
    if (hasPendingPutHandOnDeckTop(G)) {
        return;
    }
    // why: WP-702 / D-24521 — block-all guard: a parked reveal-top discard-or-keep choice
    // freezes the board until the player dispositions every revealed deck top (discard or keep).
    if (hasPendingRevealTopDispose(G)) {
        return;
    }
    // why: WP-753 / D-24580 — block-all guard: a parked reveal-three assignment freezes the
    // board until the player assigns every revealed card (draw / discard / KO).
    if (hasPendingRevealThreeAssign(G)) {
        return;
    }
    // why: WP-681 / D-24498 — block-all guard: a parked Do-Over accept/decline choice
    // freezes the board until the player accepts (discard hand + draw 4) or declines.
    if (hasPendingDoOver(G)) {
        return;
    }
    // why: block-all — pendingVictoryPileCardPick must be resolved before any other action (D-24067)
    if (hasPendingVictoryPileCardPick(G)) {
        return;
    }
    // why: block-all — pendingDrawOrEmpowered must be resolved before any other action (D-24069)
    if (hasPendingDrawOrEmpowered(G)) {
        return;
    }
    // why: block-all — pendingCoveringFireChoices must be resolved before any other action (WP-719 / D-24541)
    if (hasPendingCoveringFireChoice(G)) {
        return;
    }
    // why: block-all — pendingMoveVillainChoices must be resolved before any other action (WP-795 / D-24664)
    if (hasPendingMoveVillainChoice(G)) {
        return;
    }
    // why: block-all — pendingSplitFaceChoices must be resolved before any other action (WP-724 / D-24546)
    if (hasPendingSplitFaceChoice(G)) {
        return;
    }
    // why: block-all -- pendingCountScaledChoice must be resolved before any other action (WP-675 / D-24490)
    if (hasPendingCountScaledChoice(G)) {
        return;
    }
    // why: block-all — pendingUndercoverChoice must be resolved before any other action (WP-678 / D-24494)
    if (hasPendingUndercoverChoice(G)) {
        return;
    }
    // why: block-all — pendingReturnZeroCostDiscard must be resolved before any other action (D-24139)
    if (hasPendingReturnZeroCostDiscard(G)) {
        return;
    }
    // why: block-all — pendingDiscardToPlay must be resolved before any other action (WP-383 / D-24184)
    if (hasPendingDiscardToPlay(G)) {
        return;
    }
    // why: block-all — pendingReturnOnDiscard must be resolved before any other action (WP-498 / D-24301)
    if (hasPendingReturnOnDiscard(G)) {
        return;
    }
    // why: block-all — pendingGiveHqHeroChoice (interactive give-HQ-Hero pick, Paibok Fight) must be resolved before any other action (WP-532 / D-24343)
    if (hasPendingGiveHqHeroChoice(G)) {
        return;
    }
    // why: block-all — pendingCopyPowersChoice (interactive copy-a-Hero pick, Rogue's Copy Powers) must be resolved before any other action (WP-535 / D-24345)
    if (hasPendingCopyPowersChoice(G)) {
        return;
    }
    // why: block-all — a non-active/multi-seat pending seat choice freezes every action move
    // until all addressed seats resolve (only resolveSeatChoice proceeds) (WP-684 / D-24501)
    if (hasPendingSeatChoice(G)) {
        return;
    }
    // Step 3: Mutate G
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    // why: WP-701 / D-24520 — end-of-turn cleanup: discard in-play + hand, then draw the
    // player's new hand to HAND_SIZE. Replaces the former inline discard sweep — the draw
    // moved here from the start-of-turn onBegin auto-draw so a player holds a full hand
    // during opponents' turns (Master Strikes now land). Runs BEFORE the extra-turn branch
    // so a queued extra turn (D-24513) begins with the freshly drawn hand.
    applyEndOfTurnCleanup(G, playerID, { random });
    // why: WP-696 / D-24513 — this is the player-initiated end-turn path, a DIRECT
    // events.endTurn() that does NOT route through advanceTurnStage (D-22002 two-path
    // model), so it must honor a queued extra turn ITSELF or the extra turn is dropped
    // on the primary user path (AC-2). When the acting seat defeated "Secrets of Time
    // Travel" this turn, end the turn but begin the SAME seat's next turn via
    // boardgame.io's `events.endTurn({ next })` — a full fresh turn (onEnd -> onBegin),
    // never manual player rotation. consumeExtraTurn decrements-to-delete so normal
    // end-turns stay byte-identical.
    if (consumeExtraTurn(G, playerID)) {
        events.endTurn({ next: playerID });
        return;
    }
    // why: boardgame.io manages player rotation via ctx.events.endTurn().
    // Manual player index rotation is forbidden — the framework handles
    // advancing to the next player and resetting turn state.
    events.endTurn();
}
//# sourceMappingURL=coreMoves.impl.js.map